import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { formatDate, formatDateTime } from "../services/datetime";
import { fetchCheckout, newIdempotencyKey, takePayment } from "../services/paymentService";
import { formatCode, formatCodeInput, normalizeCode } from "../services/shortCode";
import BillBreakdown from "./BillBreakdown";
import { labelFor } from "../services/enumLabels";

/**
 * BIZ-17 — settling a booking at the desk.
 *
 * ## Deliberately short
 *
 * Somebody is standing at the counter with their coat on. The flow is: here is
 * what is owed, here is how they are paying, done. Everything else — the
 * deposit split, the receipt, the refund — is either a preset button or
 * somewhere else entirely.
 *
 * ## The numbers come from the server, every time
 *
 * Total, paid and outstanding are recomputed by the API from the booking and
 * its payments, and reloaded after each one. This screen never adds up a total
 * itself: `docs/CONVENTIONS.md` is explicit that a client-supplied total is
 * input to validate, never a value to trust, and the till is exactly where that
 * matters.
 *
 * ## A loyalty reward, optionally
 *
 * When the API sends a `loyalty` section (the plan includes loyalty, this user
 * may redeem, the client has a card here) one reward can be used in the
 * payment: one to claim now, or one the client already claimed. Picking it asks
 * the server for a quote — subtotal, the reward's discount, total due — and
 * the server claims / uses the reward in the same transaction as the payment.
 * Only methods that settle at the desk can go with a reward.
 *
 * ## One key per attempt
 *
 * The same rule the booking modal follows. The key is made when the form opens
 * and reused across retries, so a dropped connection at the moment of payment
 * settles once rather than twice.
 */
export default function CheckoutModal({ appointment, open, onClose, onPaid }) {
  const { t, i18n } = useTranslation();

  const [summary, setSummary] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [purpose, setPurpose] = useState("full");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  // The chosen reward: { kind: "reward" | "claimed", id } or null.
  const [choice, setChoice] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  const load = useCallback(async () => {
    if (!appointment) return;

    setLoading(true);
    setError("");
    setChoice(null);
    setQuote(null);
    setQuoteError("");
    try {
      const data = await fetchCheckout(appointment.id);
      setSummary(data);
      setAmount(data.outstanding);
      // Only offer what the gateway behind this branch can actually take.
      setMethod(data.methods?.[0] ?? "cash");
    } catch (err) {
      setError(apiErrorMessage(err, t("checkout.loadFailed")));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [appointment, t]);

  useEffect(() => {
    if (!open) return;

    setFieldErrors({});
    setPurpose("full");
    setIdempotencyKey(newIdempotencyKey());
    load();
  }, [open, load]);

  /** Pick a reward (or none): the server quotes the totals with it applied. */
  const choose = async (next) => {
    setChoice(next);
    setQuoteError("");
    setFieldErrors({});

    if (!next) {
      setQuote(null);
      setAmount(summary.outstanding);
      return;
    }

    setQuoting(true);
    try {
      const data = await fetchCheckout(appointment.id, {
        ...(next.kind === "claimed" ? { redemptionId: next.id } : { rewardId: next.id }),
        lineId: next.lineId,
      });
      const fresh = data.loyalty?.quote ?? null;
      setQuote(fresh);
      setAmount(fresh?.total_due ?? data.outstanding);
      setPurpose("full");
      // A reward goes only with a payment settled at the desk.
      const desk = data.desk_methods ?? [];
      if (!desk.includes(method)) setMethod(desk[0] ?? method);
    } catch (err) {
      setQuote(null);
      setChoice(null);
      setAmount(summary.outstanding);
      const fields = apiFieldErrors(err);
      setQuoteError(
        fields.loyalty_reward_id?.[0] ??
          fields.loyalty_redemption_id?.[0] ??
          fields.loyalty_line_id?.[0] ??
          apiErrorMessage(err, t("checkout.loyalty.quoteFailed"))
      );
    } finally {
      setQuoting(false);
    }
  };

  if (!open || !appointment) return null;

  const symbol = summary?.currency?.symbol ?? "";
  const settled = summary && Number(summary.outstanding) === 0;
  const methods = choice ? (summary?.desk_methods ?? []) : (summary?.methods ?? []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setFieldErrors({});

    try {
      const result = await takePayment(
        appointment.id,
        {
          amount,
          method,
          purpose,
          rewardId: choice?.kind === "reward" ? choice.id : undefined,
          redemptionId: choice?.kind === "claimed" ? choice.id : undefined,
          // A free service: the line the quote made free.
          lineId: choice ? (quote?.line_id ?? undefined) : undefined,
        },
        idempotencyKey
      );

      onPaid?.(result);

      // A second payment against the same booking is a new operation, so it
      // needs its own key — the balance after a deposit, most often.
      setIdempotencyKey(newIdempotencyKey());
      await load();
    } catch (err) {
      setFieldErrors(apiFieldErrors(err));
      setError(apiErrorMessage(err, t("checkout.failed")));
      setIdempotencyKey(newIdempotencyKey());
    } finally {
      setSaving(false);
    }
  };

  const money = (value) => (
    <span dir="ltr" className="tabular-nums">
      {symbol} {value}
    </span>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-lg p-5">
        <h2 className="mb-1 text-lg font-bold">{t("checkout.title")}</h2>
        <p className="mb-4 text-sm text-ink-2">
          {t("checkout.subtitle", { reference: appointment.reference })}
        </p>

        {loading ? (
          <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : !summary ? (
          <p className="field-error">{error || t("checkout.loadFailed")}</p>
        ) : (
          <>
            {/* What is owed. Server-computed, reloaded after every payment. */}
            <div className="mb-4 rounded-lg border border-line p-3 text-sm">
              {/* Subtotal / discount / tax / total from the server — with the
                  picked reward applied when there is one (bill_after). */}
              <BillBreakdown bill={quote?.bill_after ?? summary.bill} currency={summary.currency} />
              {quote && (
                <p className="mt-1 text-xs text-ink-2">
                  {t("checkout.loyalty.discountLine", { name: quote.reward?.name ?? "" })}{" "}
                  <span className="text-accent-text">− {money(quote.discount)}</span>
                </p>
              )}
              <div className="mt-2 flex justify-between border-t border-line pt-2">
                <span className="text-ink-2">{t("checkout.paid")}</span>
                {money(summary.paid)}
              </div>

              {quote ? (
                <div className="mt-1 flex justify-between border-t border-line pt-1 text-base font-semibold">
                  <span>{t("checkout.loyalty.totalDue")}</span>
                  {money(quote.total_due)}
                </div>
              ) : (
                <div className="mt-1 flex justify-between border-t border-line pt-1 text-base font-semibold">
                  <span>{t("checkout.outstanding")}</span>
                  {money(summary.outstanding)}
                </div>
              )}
            </div>

            {settled ? (
              <p className="rounded-lg border border-accent-line bg-accent-wash px-3 py-2 text-sm text-accent-text">
                {t("checkout.settled")}
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                {summary.loyalty && (
                  <LoyaltyRewardSection
                    loyalty={summary.loyalty}
                    choice={choice}
                    onChoose={choose}
                    busy={quoting || saving}
                    error={
                      quoteError ||
                      fieldErrors.loyalty_reward_id?.[0] ||
                      fieldErrors.loyalty_redemption_id?.[0] ||
                      fieldErrors.loyalty_line_id?.[0] ||
                      ""
                    }
                    money={money}
                    language={i18n.language}
                  />
                )}

                {/* A free service with several matching lines: staff picks which is free. */}
                {choice && quote?.line_choices?.length > 1 && (
                  <div>
                    <label className="field-label" htmlFor="checkout-free-line">
                      {t("checkout.loyalty.freeLine")}
                    </label>
                    <select
                      id="checkout-free-line"
                      className="field-input"
                      value={quote.line_id ?? ""}
                      disabled={quoting || saving}
                      onChange={(e) => choose({ ...choice, lineId: e.target.value })}
                    >
                      {quote.line_choices.map((line) => (
                        <option key={line.id} value={line.id}>
                          {line.service} — {money(line.amount)}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-ink-2">{t("checkout.loyalty.freeLineHint")}</p>
                  </div>
                )}

                <div>
                  <label className="field-label" htmlFor="checkout-amount">
                    {t("checkout.amount")} <span className="text-danger-text">*</span>
                  </label>
                  <input
                    id="checkout-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    dir="ltr"
                    className="field-input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  {fieldErrors.amount && <p className="field-error">{fieldErrors.amount[0]}</p>}

                  {/* The two things reception actually presses. */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-full bg-raised px-3 py-1 text-xs text-ink"
                      onClick={() => {
                        setAmount(quote ? quote.total_due : summary.outstanding);
                        setPurpose("full");
                      }}
                    >
                      {t("checkout.payInFull")}
                    </button>
                    {!quote && Number(summary.suggested_deposit) > 0 && (
                      <button
                        type="button"
                        className="rounded-full bg-raised px-3 py-1 text-xs text-ink"
                        onClick={() => {
                          setAmount(summary.suggested_deposit);
                          setPurpose("deposit");
                        }}
                      >
                        {t("checkout.takeDeposit", { amount: summary.suggested_deposit })}
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="field-label" htmlFor="checkout-method">
                    {t("checkout.method")} <span className="text-danger-text">*</span>
                  </label>
                  <select
                    id="checkout-method"
                    className="field-input"
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                  >
                    {methods.map((option) => (
                      <option key={option} value={option}>
                        {labelFor("payment_method", option)}
                      </option>
                    ))}
                  </select>
                  {choice && <p className="mt-1 text-xs text-ink-2">{t("checkout.loyalty.deskOnly")}</p>}
                  {fieldErrors.method && <p className="field-error">{fieldErrors.method[0]}</p>}
                </div>

                {error && <p className="field-error">{error}</p>}

                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
                    {t("common.close")}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={saving || quoting || amount === "" || amount == null}
                  >
                    {saving ? t("common.saving") : t("checkout.take")}
                  </button>
                </div>
              </form>
            )}

            {/* What has already been taken, so a deposit is visible when the
                balance is being settled later. */}
            {summary.payments?.length > 0 && (
              <div className="mt-5">
                <p className="field-label">{t("checkout.alreadyPaid")}</p>
                <ul className="divide-y divide-line rounded-lg border border-line">
                  {summary.payments.map((payment) => (
                    <li
                      key={payment.id}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <span className="min-w-0">
                        <span className="font-medium" dir="ltr">
                          {payment.currency?.symbol} {payment.amount}
                        </span>
                        <span className="ms-2 text-xs text-ink-2">
                          {labelFor("payment_method", payment.method)}
                        </span>
                        {Number(payment.breakdown?.tax_amount) > 0 && (
                          <span className="ms-2 text-xs text-ink-2" dir="ltr">
                            {t("bill.inclTax", { amount: `${payment.breakdown.tax_amount} ${payment.currency?.code ?? ""}`.trim() })}
                          </span>
                        )}
                        {Number(payment.loyalty_discount) > 0 && (
                          <span className="ms-2 text-xs text-accent-text">
                            {t("checkout.loyalty.withReward", {
                              amount: `${payment.currency?.symbol ?? ""} ${payment.loyalty_discount}`.trim(),
                            })}
                          </span>
                        )}
                      </span>
                      <span className="flex-shrink-0 text-xs text-ink-2">
                        {payment.paid_at ? formatDateTime(payment.paid_at, i18n.language) : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {settled && (
              <div className="mt-4 flex justify-end">
                <button type="button" className="btn-secondary" onClick={onClose}>
                  {t("common.close")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The client's balance and the one reward to use, if any.
 *
 * Already-claimed rewards come first — they are the client's already. Every
 * option says what it would take off; one that cannot be used here is shown
 * disabled with the server's reason (wrong branch, not enough points, expired,
 * not for these services…), so reception can say why rather than guess.
 */
function LoyaltyRewardSection({ loyalty, choice, onChoose, busy, error, money, language }) {
  const { t } = useTranslation();
  // A code the customer shows (K7M-4QX2): picks their matching claimed reward.
  const [typedCode, setTypedCode] = useState("");
  const [codeError, setCodeError] = useState("");

  const applyTypedCode = () => {
    const bare = normalizeCode(typedCode);
    if (!bare) return;
    const match = (loyalty.claimed ?? []).find((item) => normalizeCode(item.code) === bare);

    if (!match) {
      setCodeError(t("checkout.loyalty.codeNotFound"));
      return;
    }
    if (!match.usable) {
      setCodeError(match.reason ?? t("checkout.loyalty.quoteFailed"));
      return;
    }
    setCodeError("");
    setTypedCode("");
    onChoose({ kind: "claimed", id: match.id });
  };
  const number = (n) => new Intl.NumberFormat(language).format(n ?? 0);
  const selected = (kind, id) => choice?.kind === kind && choice?.id === id;

  const option = (kind, item) => {
    const id = `reward-${kind}-${item.id}`;
    const cost =
      kind === "claimed"
        ? [
            item.code && t("checkout.loyalty.code", { code: item.code_display ?? formatCode(item.code) }),
            item.expires_at && t("checkout.loyalty.expires", { date: formatDate(item.expires_at, language) }),
          ]
        : [
            item.points_required > 0 &&
              t("checkout.loyalty.points", {
                count: item.points_required,
                formatted: number(item.points_required),
              }),
            item.stamps_required > 0 &&
              t("checkout.loyalty.stamps", {
                count: item.stamps_required,
                formatted: number(item.stamps_required),
              }),
          ];

    return (
      <li key={id}>
        <label
          htmlFor={id}
          className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm ${
            item.usable
              ? selected(kind, item.id)
                ? "cursor-pointer border-accent bg-accent-wash"
                : "cursor-pointer border-line hover:border-line-strong"
              : "cursor-not-allowed border-line bg-raised opacity-70"
          }`}
        >
          <input
            id={id}
            type="radio"
            name="loyalty-reward"
            className="mt-0.5 h-4 w-4 flex-shrink-0"
            disabled={!item.usable || busy}
            checked={selected(kind, item.id)}
            onChange={() => onChoose({ kind, id: item.id })}
          />
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-2">
              <span className="truncate font-medium text-ink">{item.name}</span>
              {item.usable && item.discount && (
                <span className="flex-shrink-0 text-xs font-semibold text-accent-text">
                  − {money(item.discount)}
                </span>
              )}
            </span>
            <span className="block text-xs text-ink-2">
              {[
                t(`checkout.loyalty.types.${item.type}`, { defaultValue: labelFor("reward_type", item.type) }),
                ...cost,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
            {!item.usable && item.reason && (
              <span className="mt-0.5 block text-xs text-warning-text">{item.reason}</span>
            )}
          </span>
        </label>
      </li>
    );
  };

  return (
    <fieldset className="rounded-lg border border-line p-3">
      <legend className="px-1 text-sm font-semibold text-ink">{t("checkout.loyalty.title")}</legend>
      <p className="mb-2 text-xs text-ink-2">
        {t("checkout.loyalty.balance", {
          points: number(loyalty.account?.points),
          stamps: number(loyalty.account?.stamps),
        })}
      </p>

      <ul className="max-h-72 space-y-1.5 overflow-y-auto">
        <li>
          <label
            htmlFor="reward-none"
            className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm ${
              choice ? "border-line hover:border-line-strong" : "border-accent bg-accent-wash"
            }`}
          >
            <input
              id="reward-none"
              type="radio"
              name="loyalty-reward"
              className="h-4 w-4"
              disabled={busy}
              checked={!choice}
              onChange={() => onChoose(null)}
            />
            {t("checkout.loyalty.none")}
          </label>
        </li>

        {loyalty.claimed?.length > 0 && (
          <li className="px-1 pt-1 text-xs font-medium uppercase tracking-wide text-ink-2">
            {t("checkout.loyalty.claimedHeading")}
          </li>
        )}
        {(loyalty.claimed ?? []).map((item) => option("claimed", item))}

        {loyalty.rewards?.length > 0 && (
          <li className="px-1 pt-1 text-xs font-medium uppercase tracking-wide text-ink-2">
            {t("checkout.loyalty.rewardsHeading")}
          </li>
        )}
        {(loyalty.rewards ?? []).map((item) => option("reward", item))}
      </ul>

      {(loyalty.claimed ?? []).length > 0 && (
        <div className="mt-3">
          <label className="field-label text-xs" htmlFor="checkout-reward-code">
            {t("checkout.loyalty.codeLabel")}
          </label>
          <div className="flex gap-2">
            <input
              id="checkout-reward-code"
              dir="ltr"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="field-input flex-1 font-mono"
              placeholder="K7M-4QX2"
              value={typedCode}
              onChange={(e) => {
                setTypedCode(formatCodeInput(e.target.value));
                setCodeError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyTypedCode();
                }
              }}
            />
            <button
              type="button"
              className="btn-secondary"
              disabled={busy || !typedCode}
              onClick={applyTypedCode}
            >
              {t("checkout.loyalty.useCode")}
            </button>
          </div>
          {codeError && <p className="field-error mt-1">{codeError}</p>}
        </div>
      )}

      {busy && <p className="mt-2 text-xs text-ink-2">{t("common.loading")}</p>}
      {error && <p className="field-error mt-2">{error}</p>}
    </fieldset>
  );
}
